import { Add, MoreVert } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, IconButton, Menu, MenuItem } from '@mui/material';
import { Field, FieldArray, useField, useFormikContext } from 'formik';
import { ChangeEvent, Fragment, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';

import { api } from '../../api';
import { getLuckyPrizeImage, percentSchema, useAppDispatch } from '../../lib';
import { ImagePickerField, InputField } from '../fields';
import { Section } from './components';
import { CouponSettings, CouponDelete, PlatesSection, CouponStatusPlate } from './components/coupons';
import { Campaign, GameId } from '../../types';
import { closeSidebar, configSelectors } from '../../store';
import { getCouponName, getCouponValue, getIsActive, getCouponId } from '../../lib/utils/coupon';
import { useSelector } from 'react-redux';
import { CouponInCampain, CouponWithUsages } from '../../types/coupon';
import { useTranslation } from 'react-i18next';

type UpdateSlotParams = {
  name: string;
  userCoupon: CouponWithUsages;
  coupon: CouponInCampain;
};

type ResetSlotParams = {
  name: string;
  coupon: CouponInCampain;
};

export const Coupons = () => {
  const [couponIndexSettings, setCouponIndexSettings] = useState<number | undefined>(undefined);
  const [couponIndexDelete, setCouponIndexDelete] = useState<number | undefined>(undefined);
  const [couponIndexActionsMenu, setCouponIndexActionsMenu] = useState<number | undefined>(undefined);
  const actionButtonsRefs = useRef<HTMLElement[]>([]);
  const { submitForm, setFieldValue, validateForm } = useFormikContext();
  const dispatch = useAppDispatch();
  const isWix = useSelector(configSelectors.isWix);
  const { t } = useTranslation();

  const [removedCoupon, setRemovedCoupon] = useState('');

  const userCoupons = api.useGetCouponsQuery(undefined, { refetchOnMountOrArgChange: true });

  const [campaignField] = useField<Campaign>('campaign');
  const [campaignCouponsField, campaignCouponsMeta, campaignCouponsHelpers] =
    useField<CouponInCampain[]>('campaign.wixCoupons');

  const gameId = campaignField.value.gameId;
  const isSlotsAmountConst = gameId === GameId.WHEEL;
  const canHaveEmptySlot = [GameId.WHEEL, GameId.LUCKY].includes(gameId);
  const campaignCoupons = campaignCouponsField.value;

  const closeModal = useCallback(() => {
    setCouponIndexSettings(undefined);
    setCouponIndexDelete(undefined);
  }, []);

  const closeActionsMenu = () => {
    setCouponIndexActionsMenu(undefined);
  };

  const validate = (value: string, schema: Yup.AnySchema) => {
    try {
      schema.validateSync(value);
    } catch (error) {
      return (error as any).message;
    }
  };

  const setPrizeChancesEqual = () => {
    const count = campaignCoupons.length;
    const chance = Math.round((100 / count) * 100) / 100;

    campaignCouponsHelpers.setValue(
      campaignCoupons.map((coupon) => ({
        ...coupon,
        winChance: chance,
      }))
    );

    validateForm().then(() => {
      submitForm();
    });
  };

  const validateSum = (value: CouponInCampain[]) => {
    const total = value.reduce((total, coupon) => {
      total += Number(coupon.winChance);

      return total;
    }, 0);

    if ([GameId.LUCKY, GameId.WHEEL].includes(gameId)) {
      if (Math.abs(100 - total) > 0.1) {
        return t('validation.couponsSum.wheel', { sum: total });
      }

      return '';
    }

    if (total > 100) {
      return t('validation.couponsSum.common', { sum: total });
    }

    return '';
  };

  const setMenuButtonRef = (index: number, node: HTMLElement) => {
    actionButtonsRefs.current[index] = node;
  };

  const updateSlot = ({ name, userCoupon, coupon }: UpdateSlotParams) => {
    const newValue = {
      ...coupon,
      deleted: false,
      name: getCouponName(userCoupon),
      active: getIsActive(userCoupon),
      code: getCouponValue(userCoupon),
      uuid: getCouponId(userCoupon),
      description: '',
      displayName: '',
    };

    setFieldValue(name, newValue);
  };

  const resetSlot = ({ name, coupon }: ResetSlotParams) => {
    setFieldValue(name, {
      winChance: coupon.winChance,
      name: '',
      uuid: '',
      displayName: '',
      imageSrc: '',
      description: '',
      code: '',
    });
  };

  const closeSideBar = useCallback(() => {
    dispatch(closeSidebar());
  }, [dispatch]);

  useEffect(() => {
    if (removedCoupon) {
      submitForm();
    }
  }, [removedCoupon, submitForm]);

  const modalOpen = couponIndexSettings !== undefined || couponIndexDelete !== undefined;

  return (
    <Section elevation={0} key={campaignCoupons.length}>
      <CouponsWrapper>
        <PlatesSection validationError={campaignCouponsMeta.error} />

        <Field name="campaign.wixCoupons" component={() => null} validate={validateSum} />

        <Button onClick={setPrizeChancesEqual} sx={{ marginBottom: '20px' }} size="small">
          {t('pages.campaign.coupons.setChancesEqualButton')}
        </Button>

        <FieldArray name="campaign.wixCoupons">
          {({ remove, push }) => (
            <div>
              {userCoupons.isSuccess ? (
                <CouponsList>
                  {campaignCoupons.map((campaignCoupon, index) => {
                    const userCoupon = userCoupons.data?.find((userCoupon) => {
                      return userCoupon.uuid === campaignCoupon.uuid;
                    });

                    return (
                      <Fragment key={index}>
                        <CouponWrapper>
                          <Field
                            name={`campaign.wixCoupons[${index}].uuid`}
                            component={InputField}
                            label={isWix ? 'Wix coupon' : t('pages.coupons.table.name')}
                            select
                            className="wix-coupon-id"
                            disabled={userCoupons.isLoading}
                            onChange={(event: ChangeEvent<HTMLInputElement>) => {
                              const userCoupon = userCoupons.data.find((coupon) => {
                                return coupon.uuid === event.target.value;
                              });

                              if (userCoupon) {
                                updateSlot({
                                  name: `campaign.wixCoupons[${index}]`,
                                  userCoupon,
                                  coupon: campaignCoupon,
                                });
                              } else if (event.target.value === '') {
                                resetSlot({ name: `campaign.wixCoupons[${index}]`, coupon: campaignCoupon });
                              }
                            }}
                          >
                            {canHaveEmptySlot && <MenuItem value="">{t('pages.coupons.emptySlot')}</MenuItem>}

                            {userCoupons.isSuccess &&
                              userCoupons.data.map((userCoupon, index) => (
                                <MenuItem key={index} value={getCouponId(userCoupon)}>
                                  {getCouponName(userCoupon)}
                                </MenuItem>
                              ))}
                          </Field>

                          <Field
                            name={`campaign.wixCoupons[${index}].winChance`}
                            component={InputField}
                            label={t('pages.coupons.winChance')}
                            type="number"
                            className="wix-coupon-chance"
                            validate={(value: string) => validate(value, percentSchema)}
                          />

                          <IconButton
                            title="More actions"
                            onClick={() => setCouponIndexActionsMenu(index)}
                            ref={(node) => {
                              if (node) {
                                setMenuButtonRef(index, node);
                              }
                            }}
                          >
                            <MoreVert />
                          </IconButton>

                          {userCoupons.isSuccess && (
                            <CouponStatusPlate userCoupon={userCoupon} coupon={campaignCoupon} />
                          )}
                        </CouponWrapper>

                        {gameId === GameId.LUCKY && (
                          <PrizeImageField>
                            <Field
                              name={`campaign.wixCoupons[${index}].imageSrc`}
                              component={ImagePickerField}
                              handleWixModalOpened={closeSideBar}
                              renderPreview={(value: string) => value && <Preivew src={getLuckyPrizeImage(value)} />}
                              row
                              uploadUrl="/files-uploader/upload?type=prize"
                            />
                          </PrizeImageField>
                        )}
                      </Fragment>
                    );
                  })}
                </CouponsList>
              ) : (
                <>
                  <CircularProgress />
                  <br />
                  <br />
                </>
              )}

              {!isSlotsAmountConst && (
                <Button
                  endIcon={<Add />}
                  onClick={() => {
                    push({
                      winChance: 0,
                      description: '',
                      displayName: '',
                      uuid: '',
                    });
                  }}
                >
                  {t('pages.coupons.create')}
                </Button>
              )}

              {couponIndexActionsMenu !== undefined && (
                <Menu
                  anchorEl={actionButtonsRefs.current[couponIndexActionsMenu]}
                  open={couponIndexActionsMenu !== undefined}
                  onClose={closeActionsMenu}
                >
                  <MenuItem
                    onClick={() => {
                      closeActionsMenu();
                      setCouponIndexSettings(couponIndexActionsMenu);
                    }}
                  >
                    {t('pages.coupons.additionalSettings')}
                  </MenuItem>

                  {!isSlotsAmountConst && (
                    <RedMenuItem
                      onClick={() => {
                        closeActionsMenu();
                        setCouponIndexDelete(couponIndexActionsMenu);
                      }}
                    >
                      {t('buttons.delete')}
                    </RedMenuItem>
                  )}
                </Menu>
              )}

              <Dialog open={modalOpen} onClose={closeModal} maxWidth="xs">
                {couponIndexSettings !== undefined && (
                  <CouponSettings
                    onCloseModal={() => {
                      closeModal();
                      submitForm();
                    }}
                    couponIndex={couponIndexSettings}
                  />
                )}

                {couponIndexDelete !== undefined && (
                  <CouponDelete
                    onCloseModal={closeModal}
                    onDelete={() => {
                      closeModal();
                      remove(couponIndexDelete);

                      if (campaignCoupons[0].uuid) {
                        setRemovedCoupon(campaignCoupons[0].uuid);
                      }
                    }}
                  />
                )}
              </Dialog>
            </div>
          )}
        </FieldArray>
      </CouponsWrapper>
    </Section>
  );
};

const CouponsWrapper = styled.div`
  .create-wix-coupons-info {
    margin-bottom: 20px;
    display: flex;
  }
`;

const CouponsList = styled.div`
  margin-bottom: 20px;
`;

const CouponWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;

  .wix-coupon-id {
    margin-right: 16px;
    width: 290px;
  }

  .wix-coupon-chance {
    min-width: auto;
    width: 250px;
    margin-right: 4px;
  }

  .MuiChip-root {
    margin-top: 6px;
  }
`;

const RedMenuItem = styled(MenuItem)`
  color: red;
`;

const Preivew = styled.div<{ src: string }>`
  width: 50px;
  height: 50px;
  margin-left: 4px;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${(p) => p.src});
`;

const PrizeImageField = styled.div`
  display: flex;
  align-items: center;
  margin: -12px 0 24px;
  margin-right: 4px;
`;
